import {
  Badge,
  Box,
  Card,
  createTableProps,
  formattedCurrency,
  Pagination,
  Title,
  usePagination,
  useUpdateQueryParams,
} from 'vopay';
import type { MasterVirtualAccount, ReconciliationTransaction, Timestamp } from 'vopay';
import { format, isAfter, parseISO, subWeeks } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, TableFiltersContainer } from 'UI';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import useFetchMasterVirtualAccounts from '@/hooks/useApi/useFetchMasterVirtualAccounts';
import useFetchReconciliationTransactions from '@/hooks/useApi/useFetchReconciliationTransactions';
import ReconciliationSearchForm, { ReconciliationFilters } from './ReconciliationSearchForm';
import styles from './ReconciliationPage.module.scss';

type QueryParams = {
  token: string;
  startDate: string;
  endDate: string;
  accountName: string;
  isReconciled: string;
};

const PAGE_SIZE = 20;

const ReconciliationPage = (): React.JSX.Element => {
  const { t } = useTranslation('translations', { keyPrefix: 'Reconciliation.ReconciliationPage' });
  const { getModuleTitle } = useNavMenu();
  const { permissions } = usePermissions();
  const alertDialog = useAlertDialog();
  const [, setLoading] = useDataFetchIndicator();
  const { fetchMasterVirtualAccounts } = useFetchMasterVirtualAccounts();
  const { fetchReconciliationTransactions } = useFetchReconciliationTransactions();

  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const urlToken = queryParams.get('token') || '';
  const urlStartDate = queryParams.get('startDate') || '';
  const urlEndDate = queryParams.get('endDate') || '';
  const urlAccountName = queryParams.get('accountName') || '';
  const urlIsReconciled = queryParams.get('isReconciled') || '';

  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const weekAgo = useMemo(() => format(subWeeks(new Date(), 1), 'yyyy-MM-dd'), []);

  const startDate = urlStartDate || weekAgo;
  const endDate = urlEndDate || today;

  const initialTimeRange = useMemo<[Timestamp, Timestamp]>(
    () => [parseISO(startDate).getTime() as Timestamp, parseISO(endDate).getTime() as Timestamp],
    [startDate, endDate],
  );

  const [transactions, setTransactions] = useState<ReconciliationTransaction[]>([]);
  const [masterAccounts, setMasterAccounts] = useState<MasterVirtualAccount[]>([]);
  const [activeToken, setActiveToken] = useState(urlToken);

  const [totalRecords, setTotalRecords] = useState(0);
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalRecords, { pageSize: PAGE_SIZE });

  const filtersApplied = useMemo(
    () => Boolean(urlToken || urlAccountName || urlIsReconciled || urlStartDate || urlEndDate),
    [urlToken, urlAccountName, urlIsReconciled, urlStartDate, urlEndDate],
  );
  const masterAccountOptions = useMemo(
    () =>
      masterAccounts.map((account) => ({
        label: `${account.ShortForm} - ${account.AccountNumber || account.Token}`,
        value: account.Token,
      })),
    [masterAccounts],
  );

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        const res = await fetchMasterVirtualAccounts();
        if (!res.Success) throw new Error(res.ErrorMessage);
        setMasterAccounts(res.MasterAccounts);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'An error occurred while fetching master accounts';
        alertDialog.error('Error', msg);
      } finally {
        setLoading(false);
      }
    };

    void fetchAccounts();
  }, []);

  const fetchPage = async (token: string, offset = 0, filters?: Partial<QueryParams>) => {
    const resolvedStartDate = filters?.startDate ?? startDate;
    const resolvedEndDate = filters?.endDate ?? endDate;
    const resolvedAccountName = filters?.accountName ?? urlAccountName;
    const resolvedIsReconciled = filters?.isReconciled ?? urlIsReconciled;

    if (!token) return;
    if (isAfter(parseISO(resolvedStartDate), parseISO(resolvedEndDate))) {
      return;
    }

    try {
      setLoading(true);
      const res = await fetchReconciliationTransactions({
        Token: token,
        StartDate: resolvedStartDate,
        EndDate: resolvedEndDate,
        AccountName: resolvedAccountName || undefined,
        IsReconciled: resolvedIsReconciled === 'true' ? true : resolvedIsReconciled === 'false' ? false : undefined,
        Limit: PAGE_SIZE,
        Offset: offset,
      });

      if (!res.Success) throw new Error(res.ErrorMessage);

      setTransactions(res.Transactions);
      setTotalRecords(res.PaginationMetaData?.TotalRecords ?? 0);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred while fetching reconciliation data';
      alertDialog.error('Error', msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values: ReconciliationFilters) => {
    const newStartDate = format(new Date(values.timeRange[0]), 'yyyy-MM-dd');
    const newEndDate = format(new Date(values.timeRange[1]), 'yyyy-MM-dd');

    setActiveToken(values.token);
    updateQueryParams({
      token: values.token,
      startDate: newStartDate,
      endDate: newEndDate,
      accountName: values.accountName,
      isReconciled: values.isReconciled,
    });

    const offset = setPageIndex(0);
    void fetchPage(values.token, offset, {
      token: values.token,
      startDate: newStartDate,
      endDate: newEndDate,
      accountName: values.accountName,
      isReconciled: values.isReconciled,
    });
  };

  useEffect(() => {
    if (masterAccountOptions.length > 0) {
      if (urlToken) {
        setActiveToken(urlToken);
        const offset = setPageIndex(0);
        fetchPage(urlToken, offset, {
          token: urlToken,
          startDate,
          endDate,
          accountName: urlAccountName,
          isReconciled: urlIsReconciled,
        });
      } else {
        const firstToken = masterAccountOptions[0].value;
        setActiveToken(firstToken);
        fetchPage(firstToken);
      }
    }
  }, [masterAccountOptions]);

  return (
    <div className={styles.container}>
      <Box jc="sb" ai="center">
        <Title title={getModuleTitle()} />
      </Box>

      <Card style={{ padding: '16px', minWidth: 0, overflow: 'hidden' }}>
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <TableFiltersContainer.Trigger />
          <TableFiltersContainer.Content>
            <ReconciliationSearchForm
              initialValues={{
                timeRange: initialTimeRange,
                token: activeToken || (masterAccountOptions.length > 0 ? masterAccountOptions[0].value : ''),
                accountName: urlAccountName,
                isReconciled: urlIsReconciled,
              }}
              masterAccountOptions={masterAccountOptions}
              onSearch={handleSearch}
            />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>
        <div className={styles.tableScroll}>
          <Table
            {...createTableProps({
              id: 'reconciliationTransactionsTable',
              zebra: true,
              columns: [
                {
                  id: 'BankAccount',
                  header: t('Table.bankAccount', { defaultValue: 'Bank Account' }),
                  align: 'left',
                  field: 'BankAccount',
                },
                {
                  id: 'VirtualAccount',
                  header: t('Table.virtualAccount', { defaultValue: 'Virtual Account' }),
                  align: 'left',
                  field: 'VirtualAccount',
                  visible: permissions.VirtualAccountsEnabled,
                },
                {
                  id: 'TransactionDate',
                  header: t('Table.transactionDate', { defaultValue: 'Trans. Date' }),
                  align: 'left',
                  field: 'TransactionDate',
                },
                {
                  id: 'TransactionID',
                  header: t('Table.transactionId', { defaultValue: 'Trans. ID' }),
                  align: 'left',
                  field: 'TransactionID',
                },
                {
                  id: 'AccountName',
                  header: t('Table.accountName', { defaultValue: 'Account Name' }),
                  align: 'left',
                  field: 'AccountName',
                },
                {
                  id: 'TransactionType',
                  header: t('Table.type', { defaultValue: 'Type' }),
                  align: 'left',
                  field: 'TransactionService',
                },
                {
                  id: 'Credit',
                  header: t('Table.credit', { defaultValue: 'Credit' }),
                  align: 'right',
                  template: ({ data }) =>
                    data.TransactionType === 'CREDIT' ? `${data.Currency} ${formattedCurrency(data.Amount)}` : '',
                },
                {
                  id: 'Debit',
                  header: t('Table.debit', { defaultValue: 'Debit' }),
                  align: 'right',
                  template: ({ data }) =>
                    data.TransactionType === 'DEBIT' ? `${data.Currency} ${formattedCurrency(data.Amount)}` : '',
                },
                {
                  id: 'TransactionDescription',
                  header: t('Table.transactionDescription', { defaultValue: 'Trans. Description' }),
                  align: 'left',
                  field: 'TransactionDescription',
                },
                {
                  id: 'PostedDate',
                  header: t('Table.postedDate', { defaultValue: 'Posted Date' }),
                  align: 'left',
                  field: 'PostedDate',
                },
                {
                  id: 'CounterpartyName',
                  header: t('Table.counterpartyName', { defaultValue: 'Counterparty Name' }),
                  align: 'left',
                  field: 'CounterpartyName',
                },
                {
                  id: 'CounterpartyAccount',
                  header: t('Table.counterpartyAccount', { defaultValue: 'Counterparty Account' }),
                  align: 'left',
                  field: 'CounterpartyAccount',
                },
                {
                  id: 'IsReconciled',
                  header: t('Table.isReconciled', { defaultValue: 'Reconciled' }),
                  align: 'left',
                  template: ({ data }) => (
                    <Badge
                      color={data.IsReconciled ? 'positive' : 'neutral'}
                      label={
                        data.IsReconciled
                          ? t('Table.reconciledYes', { defaultValue: 'Yes' })
                          : t('Table.reconciledNo', { defaultValue: 'No' })
                      }
                    />
                  ),
                },
              ],
              rows: transactions.map((transaction) => ({
                id: `${transaction.TransactionID}-${transaction.PostedDate}`,
                data: transaction,
              })),
            })}
          />
        </div>
        {pageCount > 1 && (
          <Box width="100%" jc="center" mt="lg" mb="sm">
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => {
                const offset = setPageIndex(e.selected);
                void fetchPage(activeToken, offset);
              }}
            />
          </Box>
        )}
      </Card>
    </div>
  );
};

export default ReconciliationPage;
